import {
    ArrowsInLineVertical,
    CirclesThreePlus,
    Code,
    FileJs,
    FileSearch,
    Gauge,
    GearSix,
    IdentificationCard,
    ImageSquare,
    Layout,
    Package,
    PaintBrush,
    ShareNetwork,
    TerminalWindow,
    ToggleRight,
    Users,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import type React from 'react';
import type { TabType } from '../../types/tab';
import ButtonIcon from '../buttons/ButtonIcon';
import ConnectorIcon from '../icons/ConnectorIcon';
import FlowIcon from '../icons/FlowIcon';
interface Props {
    title: string | null;
    tabKey: string;
    elementId: string | null;
    isActive: boolean;
    tabType: TabType;
    showCloseButton: boolean;
    showTitle: boolean;
    onSelect: (tabType: TabType, tabKey: string) => void;
    onClose: (tabKey: string) => void;
}

const TabItem = ({
    tabKey,
    elementId,
    isActive,
    tabType,
    title,
    showCloseButton,
    showTitle,
    onSelect,
    onClose,
}: Props) => {
    const handleOnClose = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        onClose(tabKey);
    };

    const renderIcon = () => {
        const props = {
            width: 20,
            height: 20,
        };

        switch (tabType) {
            case 'flows':
            case 'flow':
                return <FlowIcon {...props} />;
            case 'pages':
            case 'page':
            case 'newPage':
                return <Layout {...props} />;
            case 'environments':
            case 'environment':
                return <GearSix {...props} />;
            case 'values':
                return <CirclesThreePlus {...props} />;
            case 'services':
                return <ConnectorIcon {...props} />;
            case 'types':
                return <Package {...props} />;
            case 'identityProviders':
                return <Users {...props} />;
            case 'assets':
                return <ImageSquare {...props} />;
            case 'api':
                return <ShareNetwork {...props} />;
            case 'importExport':
                return <ArrowsInLineVertical {...props} />;
            case 'themes':
                return <PaintBrush {...props} />;
            case 'players':
                return <TerminalWindow {...props} />;
            case 'macros':
                return <Code {...props} />;
            case 'dashboard':
                return <Gauge {...props} />;
            case 'auditing':
                return <FileSearch {...props} />;
            case 'tenant':
                return <IdentificationCard {...props} />;
            case 'organization':
                return <IdentificationCard {...props} />;
            case 'features':
                return <ToggleRight {...props} />;
            case 'components':
                return <FileJs {...props} />;
            default:
                return null;
        }
    };

    const renderTitle = () => {
        if (!showTitle && isActive === false) {
            return null;
        }

        return <span className="tab-header-item-label">{title}</span>;
    };

    const renderCloseButton = () => {
        if ((!showCloseButton && isActive === false) || tabType === 'flows') {
            return null;
        }

        const className = classNames(
            'tab-icon-wrapper',
            'close-tab',
            `close-tab-${tabType.toLowerCase()}`,
        );

        return (
            <ButtonIcon
                glyph="cross"
                className={className}
                iconClass="tab-icon"
                onClick={handleOnClose}
                title={`Close tab ${title}`}
            />
        );
    };

    const handleSelect = () => {
        if (!isActive) {
            onSelect(tabType, tabKey);
        }
    };

    const classes = classNames({
        'tab-header-item': true,
        active: isActive,
        'center-content icon-only': !showTitle,
    });

    return (
        <div
            className={classes}
            data-tab-type={tabType}
            data-tab-key={tabKey}
            data-elementid={elementId ?? ''}
            onClick={handleSelect}
            onKeyDown={({ key }) => {
                if (key !== 'Enter' && key !== ' ') {
                    return;
                }
                handleSelect();
            }}
            tabIndex={0}
            title={title ?? ''}
            // biome-ignore lint/a11y/useSemanticElements: Would require redesign
            role="button"
        >
            <div className="glyphicon">{renderIcon()}</div>
            {renderTitle()}
            {renderCloseButton()}
        </div>
    );
};

export default TabItem;
